import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '../../lib/api';
import { createLatestSaveQueue, type LatestSaveOutcome } from '../../lib/latestSaveQueue';
import { extractTitle } from './extractTitle';
import type { SaveResult } from '../../../shared/types';

const AUTOSAVE_DEBOUNCE_MS = 1500;

export type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'stale' | 'error';

interface AutosaveOptions {
  scriptId: string;
  initialVersion: number;
  onVersionChanged: (version: number) => void;
  /** 2A: each successful save reports whether the working copy now differs
   *  from what the client reads. */
  onSaved?: (result: SaveResult) => void;
}

export function useAutosave({ scriptId, initialVersion, onVersionChanged, onSaved }: AutosaveOptions) {
  const [state, setState] = useState<SaveState>('clean');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const versionRef = useRef(initialVersion);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scriptIdRef = useRef(scriptId);
  const onVersionChangedRef = useRef(onVersionChanged);
  const onSavedRef = useRef(onSaved);
  const saveAttemptRef = useRef<(doc: unknown) => Promise<LatestSaveOutcome>>(async () => 'retry');
  const queueRef = useRef(
    createLatestSaveQueue((doc: unknown) => saveAttemptRef.current(doc)),
  );

  useEffect(() => {
    versionRef.current = initialVersion;
  }, [initialVersion]);

  scriptIdRef.current = scriptId;
  onVersionChangedRef.current = onVersionChanged;
  onSavedRef.current = onSaved;

  saveAttemptRef.current = async (doc: unknown): Promise<LatestSaveOutcome> => {
    try {
      const result = await adminApi.saveScript(scriptIdRef.current, {
        expected_version: versionRef.current,
        doc_json: JSON.stringify(doc),
        title: extractTitle(doc as never),
      });
      if (result.ok) {
        versionRef.current = result.doc_version;
        onVersionChangedRef.current(result.doc_version);
        onSavedRef.current?.(result);
        return 'saved';
      }
      if (result.error === 'stale') {
        setState('stale');
        return 'retry';
      }
      setState('error');
      setErrorMsg('Save failed.');
      return 'retry';
    } catch (err) {
      setState('error');
      setErrorMsg(err instanceof Error ? err.message : 'Save failed.');
      return 'retry';
    }
  };

  function schedule(doc: unknown) {
    queueRef.current.schedule(doc);
    setState('dirty');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flush();
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  async function flush(): Promise<boolean> {
    if (!queueRef.current.hasWork()) return true;
    setState('saving');
    setErrorMsg(null);
    const saved = await queueRef.current.flush();
    if (saved) setState('saved');
    return saved;
  }

  // Cleanup on unmount — best-effort flush.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (queueRef.current.hasWork()) void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPendingWork = useCallback(() => queueRef.current.hasWork(), []);

  return { state, errorMsg, schedule, flush, hasPendingWork };
}
